import api from "../api";
import {
  Discussion,
  DiscussionListResponse,
  DiscussionResponse,
  DiscussionThreadResponse,
  DiscussionFormData,
  DiscussionFilter,
  DiscussionReply,
  ReplyFormData,
} from "../../types/elearning/discussion";

export interface DiscussionServiceResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: string;
}

class DiscussionService {
  private baseUrl = "/elearning";

  // Get discussions for a course
  async getCourseDiscussions(
    courseId: string,
    filters?: DiscussionFilter
  ): Promise<DiscussionListResponse> {
    const queryParams = new URLSearchParams();

    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (Array.isArray(value)) {
            value.forEach((item) => queryParams.append(key, item.toString()));
          } else {
            queryParams.append(key, value.toString());
          }
        }
      });
    }

    const response = await api.get<DiscussionListResponse>(
      `${
        this.baseUrl
      }/courses/${courseId}/discussions?${queryParams.toString()}`
    );

    return response as unknown as DiscussionListResponse;
  }

  // Get a single discussion with replies
  async getDiscussion(discussionId: string): Promise<DiscussionThreadResponse> {
    const response = await api.get<DiscussionThreadResponse>(
      `${this.baseUrl}/discussions/${discussionId}`
    );

    return response as unknown as DiscussionThreadResponse;
  }

  // Create a new discussion
  async createDiscussion(
    courseId: string,
    discussionData: DiscussionFormData
  ): Promise<DiscussionResponse> {
    const response = await api.post<DiscussionResponse>(
      `${this.baseUrl}/courses/${courseId}/discussions`,
      discussionData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as DiscussionResponse;
  }

  // Update a discussion
  async updateDiscussion(
    discussionId: string,
    updates: Partial<DiscussionFormData>
  ): Promise<DiscussionResponse> {
    const response = await api.put<DiscussionResponse>(
      `${this.baseUrl}/discussions/${discussionId}`,
      updates,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as DiscussionResponse;
  }

  // Delete a discussion
  async deleteDiscussion(
    discussionId: string
  ): Promise<DiscussionServiceResponse> {
    const response = await api.delete<DiscussionServiceResponse>(
      `${this.baseUrl}/discussions/${discussionId}`
    );

    return response as unknown as DiscussionServiceResponse;
  }

  // Vote on a discussion
  async voteDiscussion(
    discussionId: string,
    voteType: "up" | "down"
  ): Promise<DiscussionServiceResponse> {
    const response = await api.post<DiscussionServiceResponse>(
      `${this.baseUrl}/discussions/${discussionId}/vote`,
      { voteType },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as DiscussionServiceResponse;
  }

  // Toggle pin status (teacher only)
  async togglePinDiscussion(
    discussionId: string
  ): Promise<DiscussionServiceResponse> {
    const response = await api.post<DiscussionServiceResponse>(
      `${this.baseUrl}/discussions/${discussionId}/pin`
    );

    return response as unknown as DiscussionServiceResponse;
  }

  // Moderate discussion (teacher only)
  async moderateDiscussion(
    discussionId: string,
    action: "approve" | "reject" | "delete",
    notes?: string
  ): Promise<DiscussionServiceResponse> {
    const response = await api.post<DiscussionServiceResponse>(
      `${this.baseUrl}/discussions/${discussionId}/moderate`,
      { action, notes },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as DiscussionServiceResponse;
  }

  // Create a reply
  async createReply(
    discussionId: string,
    replyData: ReplyFormData
  ): Promise<{ success: boolean; data?: DiscussionReply; message?: string }> {
    const response = await api.post(
      `${this.baseUrl}/discussions/${discussionId}/replies`,
      replyData,
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as {
      success: boolean;
      data?: DiscussionReply;
      message?: string;
    };
  }

  // Update a reply
  async updateReply(
    replyId: string,
    content: string
  ): Promise<{ success: boolean; data?: DiscussionReply; message?: string }> {
    const response = await api.put(
      `${this.baseUrl}/replies/${replyId}`,
      { content },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as {
      success: boolean;
      data?: DiscussionReply;
      message?: string;
    };
  }

  // Delete a reply
  async deleteReply(replyId: string): Promise<DiscussionServiceResponse> {
    const response = await api.delete<DiscussionServiceResponse>(
      `${this.baseUrl}/replies/${replyId}`
    );

    return response as unknown as DiscussionServiceResponse;
  }

  // Vote on a reply
  async voteReply(
    replyId: string,
    voteType: "like" | "dislike"
  ): Promise<DiscussionServiceResponse> {
    const response = await api.post<DiscussionServiceResponse>(
      `${this.baseUrl}/replies/${replyId}/vote`,
      { voteType },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as DiscussionServiceResponse;
  }

  // Moderate reply (teacher only)
  async moderateReply(
    replyId: string,
    action: "approve" | "reject"
  ): Promise<DiscussionServiceResponse> {
    const response = await api.post<DiscussionServiceResponse>(
      `${this.baseUrl}/replies/${replyId}/moderate`,
      { action },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response as unknown as DiscussionServiceResponse;
  }

  // Search discussions
  async searchDiscussions(
    courseId: string,
    query: string,
    filters?: Omit<DiscussionFilter, "search">
  ): Promise<DiscussionListResponse> {
    const searchFilters: DiscussionFilter = {
      ...filters,
      search: query,
    };

    return this.getCourseDiscussions(courseId, searchFilters);
  }

  // Get popular discussions
  async getPopularDiscussions(
    courseId: string,
    limit: number = 10
  ): Promise<DiscussionListResponse> {
    const filters: DiscussionFilter = {
      sortBy: "most_replies",
      limit,
    };

    return this.getCourseDiscussions(courseId, filters);
  }

  // Get discussions by type
  async getDiscussionsByType(
    courseId: string,
    type: Discussion["type"],
    filters?: Omit<DiscussionFilter, "type">
  ): Promise<DiscussionListResponse> {
    const typeFilters: DiscussionFilter = {
      ...filters,
      type,
    };

    return this.getCourseDiscussions(courseId, typeFilters);
  }

  // Get discussions by category
  async getDiscussionsByCategory(
    courseId: string,
    category: Discussion["category"],
    filters?: Omit<DiscussionFilter, "category">
  ): Promise<DiscussionListResponse> {
    const categoryFilters: DiscussionFilter = {
      ...filters,
      category,
    };

    return this.getCourseDiscussions(courseId, categoryFilters);
  }
}

export const discussionService = new DiscussionService();
